import { useState } from 'react'
import { Loader2, PencilLine } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { IntegrationResponse, ProviderResponse } from '@/shared/api/types'
import { updateIntegration } from './api'

/**
 * Correcting a registration.
 *
 * A base URL typed wrong at registration was, until this, permanent: the only
 * way to change one was direct database access. That is not an acceptable
 * property of a screen whose whole job is to show configuration.
 *
 * ## The If-Match dance, and why the button can disappear
 *
 * `PATCH /integrations/{id}` demands an `If-Match` header carrying the row's
 * version — a missing header is a 400 before any handler runs. The version has
 * to come from somewhere, and until prompt 26 §2 lands neither
 * `IntegrationResponse` nor the `GET`'s `ETag` carries it. So:
 *
 *  - **`version` present** → this form works, and a concurrent edit is caught by
 *    the backend's own optimistic lock rather than by last-write-wins.
 *  - **`version` absent** → the form is not offered, and the panel explains why
 *    in words. A Save button that can only ever produce a 400 is worse than no
 *    button: it teaches people the application is broken rather than that a
 *    capability is missing.
 *
 * The check is on the field, not on a version number or a feature flag, so the
 * moment the backend starts sending it this becomes editable with no further
 * change here.
 *
 * ## What is editable, and what is not
 *
 * `UpdateIntegrationRequest` accepts displayName, baseUrl, capabilities, config,
 * rateLimit and notes. This form offers all but capabilities: those are the
 * provider's declared allowlist, and narrowing them is a decision about what the
 * integration may do rather than a correction to how it is addressed. It belongs
 * with enable/disable, not in a form headed "fix the URL".
 *
 * `code`, `environment`, `provider` and `authKind` are immutable server-side —
 * `(code, environment)` is the row's identity and the other two determine which
 * adapter runs. They are shown, greyed, so that "why can't I change this?" is
 * answered on the screen rather than by trying.
 */
export function EditRegistrationButton({
  integration, descriptor, onSaved,
}: {
  integration: IntegrationResponse
  descriptor: ProviderResponse | undefined
  onSaved: () => void
}) {
  const [open, setOpen] = useState(false)
  const editable = integration.version != null

  if (!editable) return null
  return (
    <>
      <button type="button" className="erp-btn" onClick={() => setOpen(true)}>
        <PencilLine className="h-3.5 w-3.5" /> Edit
      </button>
      {open && (
        <EditRegistrationModal
          integration={integration}
          descriptor={descriptor}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onSaved() }}
        />
      )}
    </>
  )
}

/** The words shown where the Edit button would be, when the backend cannot support it. */
export function EditUnavailableNote({ integration }: { integration: IntegrationResponse }) {
  if (integration.version != null) return null
  return (
    <Note tone="info">
      Editing is not available on this backend: <code className="font-mono">PATCH</code> requires an{' '}
      <code className="font-mono">If-Match</code> version, and neither this response nor the{' '}
      <code className="font-mono">GET</code>&rsquo;s <code className="font-mono">ETag</code> carries
      one, so the browser has nothing to send. A wrong base URL currently has to be corrected in the
      database. Prompt 26 §2 adds the field, and this becomes an edit form with no further change
      here.
    </Note>
  )
}

function EditRegistrationModal({
  integration, descriptor, onClose, onSaved,
}: {
  integration: IntegrationResponse
  descriptor: ProviderResponse | undefined
  onClose: () => void
  onSaved: () => void
}) {
  const [displayName, setDisplayName] = useState(integration.displayName ?? '')
  const [baseUrl, setBaseUrl] = useState(integration.baseUrl ?? '')
  const [notes, setNotes] = useState(integration.notes ?? '')
  const [config, setConfig] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const k of descriptor?.configKeys ?? []) {
      const v = (integration.config ?? {})[k]
      initial[k] = v == null ? '' : String(v)
    }
    return initial
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)

  const keys = [...(descriptor?.configKeys ?? [])].sort()

  const save = async () => {
    setSaving(true)
    setError(null)
    setConflict(false)
    try {
      await updateIntegration(
        integration.id,
        {
          displayName: displayName.trim(),
          // An emptied URL means "use the adapter's default", which is null, not
          // "". The service only applies non-null fields, so sending "" would
          // store an empty string and the adapter would build requests against
          // nothing.
          baseUrl: baseUrl.trim() || null,
          notes: notes.trim() || null,
          // Only declared keys, and an emptied one is removed rather than stored
          // as "" — `validatedConfig` rejects anything undeclared anyway, and an
          // empty string reads as "set to nothing" on the panel afterwards.
          config: Object.fromEntries(
            Object.entries(config)
              .filter(([, v]) => v.trim() !== '')
              .map(([k, v]) => [k, v.trim()]),
          ),
        },
        integration.version!,
      )
      onSaved()
    } catch (e) {
      const message = explain(e)
      // The backend's own words for a lost optimistic lock. Worth its own
      // treatment: retrying blind would overwrite whatever the other person did.
      if (/modified concurrently|412|conflict/i.test(message)) setConflict(true)
      setError(message)
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit registration"
      description="Corrects how this integration is addressed and described. Credentials are rotated on their own tab and are never touched here."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={saving || conflict}
            onClick={() => void save()}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
          </button>
        </>
      }
    >
      <ErrorStrip message={conflict ? null : error} />

      {conflict && (
        <div className="mb-3">
          <Note tone="bad">
            Someone else changed this integration while this form was open. Nothing has been saved.
            Close and reopen to load their version — retrying from here would overwrite it.
          </Note>
        </div>
      )}

      <div className="space-y-3">
        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Display name</span>
          <input
            className="erp-input w-full mt-1"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Base URL</span>
          <input
            className="erp-input w-full mt-1 font-mono text-erp-xs"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="Empty uses the adapter's default"
          />
          <p className="text-erp-xs text-muted-foreground mt-1">
            The host the adapter calls. Changing it takes effect on the next request — there is no
            cache to clear, but an in-flight call finishes against the old one.
          </p>
        </label>

        {keys.length > 0 && (
          <div>
            <span className="text-erp-xs text-muted-foreground">
              Configuration <span className="opacity-70">— only keys this provider declares</span>
            </span>
            <div className="mt-1 space-y-2">
              {keys.map((k) => (
                <label key={k} className="flex items-center gap-2">
                  <code className="font-mono text-erp-xs w-32 shrink-0 truncate" title={k}>{k}</code>
                  <input
                    className="erp-input flex-1 min-w-0 font-mono text-erp-xs"
                    value={config[k] ?? ''}
                    onChange={(e) => setConfig((c) => ({ ...c, [k]: e.target.value }))}
                    placeholder="Not set"
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-erp-xs text-muted-foreground">Notes</span>
          <textarea
            className="erp-input w-full mt-1 min-h-[56px]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-erp-xs text-muted-foreground">
          <div className="mb-1">Fixed at registration and not editable:</div>
          <div className="pl-1 space-y-0.5">
            <div>
              code <code className="font-mono">{integration.code}</code> and environment{' '}
              <code className="font-mono">{integration.environment}</code> — together they are this
              row&rsquo;s identity
            </div>
            <div>
              provider <code className="font-mono">{integration.provider}</code> and auth{' '}
              <code className="font-mono">{integration.authKind}</code> — they decide which adapter
              runs and how it signs
            </div>
          </div>
          <div className="mt-1.5">
            To change any of them, register a second integration and disable this one.
          </div>
        </div>
      </div>
    </Modal>
  )
}
